<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { useCartStore } from '@/store/cart'
import { useAuthStore } from '@/store/auth'
import useNotifications from '@/store/notification'
import { useCurrency } from '@/composables/useCurrency'

const router = useRouter()
const cartStore = useCartStore()
const authStore = useAuthStore()
const { showNotification } = useNotifications()
const { formatPrice } = useCurrency()

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL

const paymentMethod = ref('')
const loading = ref(false)
const error = ref(null)

const cartItems = computed(() => cartStore.cart)

const calculateLeaseDuration = (item) => {
  const start = new Date(item.start_date)
  const end = new Date(item.end_date)
  const days = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1
  return Math.max(days, 1)
}

const calculateItemTotal = (item) => {
  const rate = authStore.isAuthenticated && item.item_details
    ? item.item_details.hourly_rate
    : item.item?.hourly_rate || 0
  return rate * calculateLeaseDuration(item) * item.quantity
}

const subtotal = computed(() =>
  cartStore.cart.reduce((total, item) => total + calculateItemTotal(item), 0)
)

const serviceFee = computed(() => subtotal.value * 0.06)
const shippingCost = ref(0)
const orderTotal = computed(() => subtotal.value + serviceFee.value + shippingCost.value)

const getItemImage = (item) =>
  (authStore.isAuthenticated && item.item_details
    ? item.item_details.images?.[0]?.image_url
    : item.item?.images?.[0]?.image_url) || '/placeholder-image.jpg'

const getItemName = (item) =>
  (authStore.isAuthenticated && item.item_details
    ? item.item_details.name
    : item.item?.name) || 'Unknown Product'

const selectPaymentMethod = (method) => {
  paymentMethod.value = method
  error.value = null
}

const submitForm = async () => {
  if (loading.value) return
  error.value = null

  if (!paymentMethod.value) {
    showNotification('Payment Required', 'Please select a payment method to continue', 'warning')
    return
  }

  loading.value = true

  try {
    if (paymentMethod.value === 'paypal') {
      const response = await axios.post(
        `${API_BASE_URL}/api/paypal/payments/`,
        {},
        { withCredentials: true }
      )
      if (!response?.data?.approval_url) {
        throw new Error('Invalid response: Missing approval URL.')
      }
      window.location.href = response.data.approval_url
    } else {
      throw new Error('Unsupported payment method')
    }
  } catch (err) {
    error.value = err
    let msg = 'Payment failed. Please try again.'
    if (err.response) {
      const { status, data } = err.response
      if (status === 401) msg = 'Session expired. Please login again.'
      else if (status === 403) msg = 'Unauthorized. Please verify your details.'
      else if (status === 429) msg = 'Too many requests. Please wait.'
      else if (data?.error) msg = data.error
      else msg = `Payment error (${status}).`
    } else if (err.request) {
      msg = 'Network error. Check your connection.'
    } else if (err.message) {
      msg = err.message
    }
    showNotification('Payment Error', msg, 'error')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  cartStore.loadCart()
})
</script>

<template>
  <div class="min-h-screen bg-theme-bg py-6 px-4 sm:px-6 lg:px-8">
    <div class="max-w-4xl mx-auto space-y-6">

      <!-- Header -->
      <div class="bg-gradient-to-r from-primary-400 to-primary-600 text-white rounded-2xl shadow-xl p-5 sm:p-6 text-center">
        <h1 class="text-xl sm:text-2xl md:text-3xl font-extrabold tracking-tight flex items-center justify-center gap-2">
          <i class="pi pi-lock text-lg sm:text-xl"></i> Secure Checkout
        </h1>
        <p class="mt-1 text-xs sm:text-sm text-white/80">
          Review your order and complete payment
        </p>
      </div>

      <!-- Empty Cart -->
      <div
        v-if="cartItems.length === 0"
        class="flex flex-col items-center justify-center py-16 sm:py-24 text-center bg-theme-card rounded-2xl border border-theme-border shadow-sm"
      >
        <div class="flex items-center justify-center w-20 h-20 rounded-full bg-theme-bg2">
          <i class="pi pi-shopping-cart text-4xl text-theme-muted"></i>
        </div>
        <h3 class="mt-4 text-xl font-bold text-theme-text">Nothing to Checkout</h3>
        <p class="mt-2 text-sm text-theme-muted max-w-sm">
          Your cart is empty. Add some items before checking out.
        </p>
        <button
          @click="router.push('/categories')"
          class="mt-5 inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm shadow-lg shadow-primary-600/20 transition-all active:scale-[0.98]"
        >
          <i class="pi pi-search text-sm"></i> Browse Categories
        </button>
      </div>

      <!-- Checkout Content -->
      <template v-else>
        <div class="grid gap-6 lg:grid-cols-[1fr,380px]">

          <!-- LEFT: Order Summary -->
          <div class="space-y-5">
            <!-- Step 1: Review Items -->
            <section class="bg-theme-card rounded-2xl border border-theme-border shadow-sm overflow-hidden">
              <div class="px-5 py-4 border-b border-theme-border flex items-center gap-3">
                <span class="flex items-center justify-center w-7 h-7 rounded-full bg-primary-600 text-white text-xs font-bold">1</span>
                <h2 class="text-base font-bold text-theme-text">Review Items</h2>
                <span class="ml-auto text-xs text-theme-muted">{{ cartItems.length }} item{{ cartItems.length !== 1 ? 's' : '' }}</span>
              </div>

              <!-- Desktop table (md+) -->
              <div class="hidden md:block">
                <table class="w-full">
                  <thead>
                    <tr class="bg-primary-600/10 dark:bg-primary-600/20 text-theme-text uppercase text-xs">
                      <th class="py-3 px-4 text-left font-semibold border-b border-r border-theme-border">Item</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Qty</th>
                      <th class="py-3 px-4 text-center font-semibold border-b border-r border-theme-border">Duration</th>
                      <th class="py-3 px-4 text-right font-semibold border-b border-theme-border">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, idx) in cartItems"
                      :key="item.id || idx"
                      class="border-b border-theme-border last:border-b-0 transition-colors"
                      :class="idx % 2 === 0 ? 'bg-theme-card' : 'bg-theme-bg2/50'"
                    >
                      <td class="py-3 px-4 border-r border-theme-border">
                        <div class="flex items-center gap-3">
                          <img
                            :src="getItemImage(item)"
                            :alt="getItemName(item)"
                            class="w-12 h-12 rounded-xl object-cover border border-theme-border shrink-0"
                          />
                          <span class="text-sm font-medium text-theme-text truncate max-w-[200px]">
                            {{ getItemName(item) }}
                          </span>
                        </div>
                      </td>
                      <td class="py-3 px-4 text-center text-sm text-theme-text border-r border-theme-border">
                        {{ item.quantity }}
                      </td>
                      <td class="py-3 px-4 text-center border-r border-theme-border">
                        <span class="inline-flex items-center gap-1 text-xs text-theme-text2 bg-theme-bg2 px-2.5 py-1 rounded-full">
                          <i class="pi pi-calendar text-[10px]"></i>
                          {{ calculateLeaseDuration(item) }}d
                        </span>
                      </td>
                      <td class="py-3 px-4 text-right text-sm font-bold text-theme-text">
                        {{ formatPrice(calculateItemTotal(item)) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Mobile cards (<md) -->
              <div class="md:hidden divide-y divide-theme-border">
                <div
                  v-for="(item, idx) in cartItems"
                  :key="'m-' + (item.id || idx)"
                  class="flex items-center gap-3 p-4"
                >
                  <img
                    :src="getItemImage(item)"
                    :alt="getItemName(item)"
                    class="w-14 h-14 rounded-xl object-cover border border-theme-border shrink-0"
                  />
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-theme-text truncate">{{ getItemName(item) }}</p>
                    <p class="text-xs text-theme-muted mt-0.5">
                      Qty: {{ item.quantity }} &middot; {{ calculateLeaseDuration(item) }} day{{ calculateLeaseDuration(item) !== 1 ? 's' : '' }}
                    </p>
                  </div>
                  <span class="text-sm font-bold text-theme-text shrink-0">
                    {{ formatPrice(calculateItemTotal(item)) }}
                  </span>
                </div>
              </div>
            </section>

            <!-- Step 2: Payment Method -->
            <section class="bg-theme-card rounded-2xl border border-theme-border shadow-sm overflow-hidden">
              <div class="px-5 py-4 border-b border-theme-border flex items-center gap-3">
                <span class="flex items-center justify-center w-7 h-7 rounded-full bg-primary-600 text-white text-xs font-bold">2</span>
                <h2 class="text-base font-bold text-theme-text">Payment Method</h2>
              </div>

              <div class="p-5">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <!-- PayPal -->
                  <button
                    @click="selectPaymentMethod('paypal')"
                    class="relative flex items-center gap-4 p-4 rounded-xl border-2 transition-all"
                    :class="paymentMethod === 'paypal'
                      ? 'border-primary-600 bg-primary-600/5 dark:bg-primary-600/10 shadow-md'
                      : 'border-theme-border hover:border-primary-400 hover:bg-theme-bg2'"
                    :disabled="loading"
                  >
                    <div class="w-12 h-12 rounded-xl bg-[#003087] flex items-center justify-center shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" class="w-6 h-6">
                        <path fill="#ffffff" d="M111.4 295.9c-3.5 19.2-17.4 108.7-21.5 134-.3 1.8-1 2.5-3 2.5H12.3c-7.6 0-13.1-6.6-12.1-13.9L58.8 46.6c1.5-9.6 10.1-16.9 20-16.9 152.3 0 165.1-3.7 204 11.4 60.1 23.3 65.6 79.5 44 140.3-21.5 62.6-72.5 89.5-140.1 90.3-43.4 .7-69.5-7-75.3 24.2zM357.1 152c-1.8-1.3-2.5-1.8-3 1.3-2 11.4-5.1 22.5-8.8 33.6-39.9 113.8-150.5 103.9-204.5 103.9-6.1 0-10.1 3.3-10.9 9.4-22.6 140.4-27.1 169.7-27.1 169.7-1 7.1 3.5 12.9 10.6 12.9h63.5c8.6 0 15.7-6.3 17.4-14.9 .7-5.4-1.1 6.1 14.4-91.3 4.6-22 14.3-19.7 29.3-19.7 71 0 126.4-28.8 142.9-112.3 6.5-34.8 4.6-71.4-23.8-92.6z"/>
                      </svg>
                    </div>
                    <div class="text-left">
                      <p class="text-sm font-semibold text-theme-text">PayPal</p>
                      <p class="text-xs text-theme-muted">Pay securely with PayPal</p>
                    </div>
                    <div
                      v-if="paymentMethod === 'paypal'"
                      class="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center"
                    >
                      <i class="pi pi-check text-white text-[10px]"></i>
                    </div>
                  </button>

                  <!-- More methods placeholder -->
                  <div class="flex items-center justify-center p-4 rounded-xl border-2 border-dashed border-theme-border">
                    <div class="text-center">
                      <i class="pi pi-credit-card text-2xl text-theme-muted"></i>
                      <p class="text-xs text-theme-muted mt-1">More coming soon</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Back to Cart link (mobile only, below cards) -->
            <button
              @click="router.push('/cart')"
              class="lg:hidden flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium"
            >
              <i class="pi pi-arrow-left text-xs"></i> Back to Cart
            </button>
          </div>

          <!-- RIGHT: Order Total + Place Order -->
          <div class="lg:sticky lg:top-6 self-start space-y-4">
            <section class="bg-theme-card rounded-2xl border border-theme-border shadow-sm overflow-hidden">
              <div class="px-5 py-4 border-b border-theme-border">
                <h2 class="text-base font-bold text-theme-text">Order Summary</h2>
              </div>

              <div class="p-5 space-y-3">
                <div class="flex justify-between text-sm">
                  <span class="text-theme-text2">Subtotal ({{ cartItems.length }} item{{ cartItems.length !== 1 ? 's' : '' }})</span>
                  <span class="text-theme-text font-medium">{{ formatPrice(subtotal) }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-theme-text2">Service Fee (6%)</span>
                  <span class="text-theme-text font-medium">{{ formatPrice(serviceFee) }}</span>
                </div>
                <div class="flex justify-between text-sm">
                  <span class="text-theme-text2">Shipping</span>
                  <span class="text-theme-text font-medium">
                    {{ shippingCost > 0 ? formatPrice(shippingCost) : 'Free' }}
                  </span>
                </div>

                <div class="border-t border-theme-border pt-3 mt-3">
                  <div class="flex justify-between">
                    <span class="text-base font-bold text-theme-text">Total</span>
                    <span class="text-xl font-extrabold text-primary-600">{{ formatPrice(orderTotal) }}</span>
                  </div>
                </div>
              </div>

              <!-- Error -->
              <div v-if="error" class="mx-5 mb-4 p-3 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 rounded-xl text-sm flex items-start gap-2">
                <i class="pi pi-exclamation-triangle mt-0.5 shrink-0"></i>
                <span>{{ error.message || 'Something went wrong. Please try again.' }}</span>
              </div>

              <!-- Place Order Button -->
              <div class="px-5 pb-5">
                <button
                  @click="submitForm"
                  :disabled="loading || !paymentMethod"
                  class="w-full py-3.5 rounded-xl font-bold text-base transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                  :class="loading || !paymentMethod
                    ? 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-500 text-white shadow-lg shadow-primary-600/20'"
                >
                  <template v-if="loading">
                    <i class="pi pi-spinner pi-spin"></i> Processing...
                  </template>
                  <template v-else>
                    <i class="pi pi-lock text-sm"></i> Place Order &middot; {{ formatPrice(orderTotal) }}
                  </template>
                </button>

                <p v-if="!paymentMethod" class="text-center text-xs text-theme-muted mt-2">
                  Select a payment method to continue
                </p>
              </div>
            </section>

            <!-- Security badges -->
            <div class="flex items-center justify-center gap-4 text-theme-muted">
              <div class="flex items-center gap-1.5 text-xs">
                <i class="pi pi-lock text-[10px]"></i> Secure checkout
              </div>
              <div class="flex items-center gap-1.5 text-xs">
                <i class="pi pi-shield text-[10px]"></i> Buyer protection
              </div>
            </div>

            <!-- Back to Cart link (desktop) -->
            <button
              @click="router.push('/cart')"
              class="hidden lg:flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium mx-auto"
            >
              <i class="pi pi-arrow-left text-xs"></i> Back to Cart
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
